import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutGrid, Users, Target, Globe, Inbox, Sparkles, Bell,
  CheckSquare, CalendarDays, Wallet, Settings, LogOut, Command, Activity
} from "lucide-react";

const NAV = [
  { to: "/", label: "Command Center", icon: LayoutGrid, group: "Overview" },
  { to: "/campaigns", label: "Campaigns", icon: Target, group: "Operations" },
  { to: "/alerts", label: "Alerts", icon: Bell, group: "Operations" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, group: "Operations" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, group: "Operations" },
  { to: "/ai-insights", label: "AI Insights", icon: Sparkles, group: "Intelligence" },
  { to: "/sync-logs", label: "Sync Logs", icon: Activity, group: "Intelligence" },
  { to: "/clients", label: "Clients & Accounts", icon: Users, group: "Assets" },
  { to: "/landing-pages", label: "Landing Pages", icon: Globe, group: "Assets" },
  { to: "/leads", label: "Leads", icon: Inbox, group: "Assets" },
  { to: "/budget", label: "Budget & Pacing", icon: Wallet, group: "Finance" },
  { to: "/profile", label: "My Profile", icon: Users, group: "Account" },
  { to: "/admin", label: "Admin", icon: Settings, group: "System", roles: ["super_admin", "admin"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const visible = NAV.filter((n) => !n.roles || n.roles.includes(user?.role) || user?.role === "super_admin");
  const groups = visible.reduce((acc, n) => { (acc[n.group] = acc[n.group] || []).push(n); return acc; }, {});

  return (
    <aside className="w-64 border-r border-border bg-card fixed h-screen flex flex-col" data-testid="app-sidebar">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
            <Command className="w-4 h-4 text-primary-foreground" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading font-semibold text-[15px] leading-none tracking-tight">Ads Intel</div>
            <div className="label-mono mt-1">Marketing Command</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="mb-5">
            <div className="label-mono px-3 mb-2">{group}</div>
            {items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm mb-0.5 transition-colors ${
                      (isActive || active)
                        ? "bg-primary text-primary-foreground font-medium"
                        : "text-foreground hover:bg-secondary"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-border px-3 py-3">
        <div className="flex items-center gap-2.5 px-3 py-2">
          <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center text-xs font-semibold">
            {(user?.name || "U").split(" ").map(x => x[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-1.5 hover:bg-secondary rounded-sm"
            title="Logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </aside>
  );
}
